import { forwardRef, useEffect, useRef } from "react";
import { useRowSelect, useSortBy, useTable } from "react-table";
import leftPageImg from "../../assets/images/pgn-left.svg";
import rightPageImg from "../../assets/images/pgn-right.svg";
import { usePagination } from "./UsePagination";
import tabledwicn from "../../assets/images/table_dwicn.svg"
import { useDrop } from "react-dnd";
import { DroppableRow } from "./DroppableRow";
import Loader from "../loader/Loader";

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);
  return (
    <>
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          ref={resolvedRef}
          {...rest}
        />
      </div>
    </>
  );
});

const ReactTable = ({
  columns,
  data,
  title,
  perPage,
  handlePerPage,
  currentPage = 1,
  totalCount,
  siblingCount = 1,
  pageSize = 10,
  onPageChange = null,
  className = '',
  setSelectedRowsID = () => { },
  isCheckBox = false,
  selectedRowsID = 0,
  isDocumentListing = false,
  handleTagUpdate = () => { },
  isLoading = false,
  emptyMessage = "Data Not Found"
}) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
    perPage,
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              {isCheckBox && (
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              )}
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              {isCheckBox && (
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              )}
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  const perPageRecord = () => {
    return [10, 20, 50, 100];
  };
  const perPageArr = perPageRecord();

  let lastPage = paginationRange[paginationRange.length - 1];
  const onNext = () => {
    onPageChange(lastPage > currentPage ? currentPage + 1 : lastPage);
  };

  const onPrevious = () => {
    onPageChange(currentPage !== 1 ? currentPage - 1 : 1);
  };
  useEffect(() => {
    if (data?.length > 0) {
      setSelectedRowsID(selectedFlatRows);
    }
  }, [selectedRowIds]);

  return (
    <div className="">
      <div
        className={`table-responsive common-table introduction-table set-loader-pos ${className}`}
      >
        <table className="table table-striped" {...getTableProps()}>
          <thead>
            {
              // Loop over the header rows
              headerGroups.map((headerGroup, i) => (
                // Apply the header row props
                <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                  {
                    // Loop over the headers in each row
                    headerGroup.headers.map((column, index) => (
                      // Apply the header cell props
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={index}
                      >
                        <div className="table-heading">
                          {column.render("Header")}
                          {/* Add a sort direction indicator */}
                          <span>
                            {/* {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''} */}
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <span>
                                  <img
                                    src={tabledwicn}
                                    className="table-filter-arrow"
                                    alt="down"
                                  />
                                </span>
                              ) : (
                                <span>
                                  <img
                                    src={tabledwicn}
                                    className="table-filter-arrow table-filter-arrow-up"
                                    alt="up"
                                  />
                                </span>
                              )
                            ) : (
                              ""
                            )}
                          </span>
                        </div>
                      </th>
                    ))
                  }
                </tr>
              ))
            }
          </thead>
          <tbody className="position-relative" {...getTableBodyProps()}>
            {isLoading ? <Loader /> :
              <>
                {rows.length > 0 ? (
                  rows.map((row, index) => {
                    prepareRow(row);
                    return (
                      isDocumentListing ? (
                        <DroppableRow
                          key={row.id}
                          row={row}
                          index={index}
                          onDrop={(item, index, row) => {
                            handleTagUpdate(item, index, row)
                          }}
                          selectedRowsID={selectedRowsID}
                        />
                      ) : (
                        <tr {...row.getRowProps()} key={index} role="row" data-testid="tags">
                          {row.cells.map((cell, i) => {
                            return (
                              <td key={i} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                            );
                          })}
                        </tr>
                      )
                    );
                  })
                ) : (
                  <>
                    <tr>
                      <td className="nodata-found pt-3 pb-1 text-center w-100" colSpan={columns.length} style={{ maxWidth: "unset" }}>
                        {emptyMessage}
                      </td>
                    </tr>
                  </>
                )}
              </>
            }
          </tbody>
        </table>
      </div>
      <div className="pagination-main">
        <div className="row align-items-center">
          <div className="col-md-6">
            {rows.length > 0 && (
              <div className="page-note">
                Record per page
                <div className="down-icnmy">
                  <img className="img-dwn" src={tabledwicn} alt="tableicn" />
                  <select
                    className="form-select"
                    id="inputGroupSelect01"
                    onChange={(e) => handlePerPage(e.target.value)}
                    value={perPage}
                  >
                    {perPageArr?.map((page, i) => {
                      return (
                        <option value={page} key={`page-option-${i}`}>
                          {page}
                        </option>
                      );
                    })}
                  </select>
                </div>
                Showing {perPage * (currentPage - 1) + 1} to {(perPage * currentPage) < totalCount ? perPage * currentPage : totalCount} of{" "}
                {totalCount} entries
              </div>
            )}
          </div>
          <div className="col-md-6 text-end">
            {rows.length > 0 && (
              <ul className="pagination">
                <li className="page-item cursor-pointer">
                  <a
                    className="page-link"
                    onClick={() => {
                      onPrevious();
                    }}
                  >
                    <img className="" src={leftPageImg} alt="leftarroicn" />
                  </a>
                </li>
                {paginationRange.map((pageNumber, i) => {
                  if (pageNumber === "...") {
                    return (
                      <li className="pagination-item dots" key={`active-${i}`}>
                        &#8230;
                      </li>
                    );
                  }
                  return (
                    <li
                      className={`cursor-pointer page-item  ${pageNumber === currentPage ? "active" : ""
                        }`}
                      onClick={() => onPageChange(pageNumber)}
                      key={`active-page-item-${i}`}
                    >
                      <a
                        aria-controls="example1"
                        data-dt-idx={pageNumber}
                        tabIndex="0"
                        className="page-link"
                      >
                        {pageNumber}
                      </a>
                    </li>
                  );
                })}
                <li className="cursor-pointer page-item" style={{ cursor: 'pointer' }}>
                  <a
                    className="page-link"
                    onClick={() => {
                      onNext();
                    }}
                  >
                    <img className="" src={rightPageImg} alt="leftarroicn" />
                  </a>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactTable;
